import "./index.scss";

import { SelectChangeEvent, Table, TableBody, TableContainer } from "@mui/material";
import { Box } from "@mui/system";
import ConfirmationBox from "components/ConfirmationBox";
import Pagination, { PAGE_OPTIONS } from "components/Pagination";
import ScreenLoader from "components/ScreenLoader";
import {
  MEMBER_NOT_DELETABLE_ERR_MSG,
  MEMBER_STATUS_ALLOWED_FOR_DELETE,
  MSG_OPERATION_FAILED,
} from "constants/index";
import ActionButtons from "containers/MemberTasks/ActionButtons";
import MassInstructorSwitchModal from "containers/MemberTasks/MassInstructorSwitchModal";
import MassStatusUpdateModal from "containers/MemberTasks/MassStatusUpdateModal";
import { useQueryParams } from "hooks/useQueryParams";
import { TPagination } from "modules/api/type";
import { getLkpInstructorList } from "modules/lookup/lookupServices";
import { getMemberList, massDelete, updateMemberStatus } from "modules/member/memberServices";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { TableHeader, TableItem } from "./TableComponents";

const AssignedMembers = ({ staff }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [params, setParams, defaultParams, setObjParams] = useQueryParams({
    instructor_ids: `${staff?.id}`,
    limit: 10,
    page: 1,
  });
  const [pagination, setPagination] = useState<TPagination>({
    current_page: 0,
    data_from_offset: 0,
    data_to_offset: 0,
    per_page: PAGE_OPTIONS[0],
    total_pages: 0,
    total_records: 0,
  });
  const [checkAllIsChecked, setCheckAllIsChecked] = useState(false);
  const [checkedEntries, setCheckedEntries] = useState<number[]>([]);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [showInstructorSwitchModal, setShowInstructorSwitchModal] = useState<boolean>(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState<boolean>(false);
  const [showMassDeleteWarningModal, setShowMassDeleteWarningModal] = useState<boolean>(false);

  useEffect(() => {
    getLkpInstructorList({ limit: 0, sort_by: "LAST_NAME" }).then((res) => {
      let instructorOpts: any[] = [{ isSelectAll: true, label: "All Instructors", value: "" }];
      if (res.success) {
        res.data?.forEach((e) =>
          instructorOpts.push({
            label: `${e.last_name} ${e.first_name}`,
            value: e.instructor_id,
            ...e,
          })
        );
      }
      setInstructorOptions(instructorOpts);
    });
  }, []);

  const fetchData = useCallback(
    async (parameters) => {
      if (loading) return;
      setLoading(true);
      let result = await getMemberList(parameters);
      setLoading(false);
      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
      }
    },
    [setData, setPagination, loading, setLoading]
  );

  useEffect(() => {
    fetchData(params);
  }, [params]);

  const handleSortChange = (sort_by: string, sort_order: string) => {
    let temp = { ...params, page: 1, sort_by, sort_order };
    setObjParams({
      page: 1,
      sort_by,
      sort_order,
    });
    fetchData(temp);
  };

  const handlePaginationOptionChange = (event: SelectChangeEvent<any>) => {
    let temp = { ...params, limit: event.target.value, page: 1 };
    setParams("limit", event.target.value);
    fetchData(temp);
  };

  const handlePageChange = (page: number) => {
    let temp = { ...params, page };
    setParams("page", page);
    fetchData(temp);
  };

  const handleMassStatusSwitch = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowStatusUpdateModal(true);
  };

  const handleMassInstructorSwitch = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowInstructorSwitchModal(true);
  };

  const handleMassDelete = () => {
    if (checkedEntries.length === 0) {
      toast("You should first select members from the grid", { type: "error" });
      return;
    }
    setShowMassDeleteWarningModal(true);
  };

  const handleSignleEntryInstructorSwitch = (member) => {
    setSelectedMember(member);
    setShowInstructorSwitchModal(true);
  };

  const handleSignleEntryDelete = (member) => {
    if (!MEMBER_STATUS_ALLOWED_FOR_DELETE.includes(member.status)) {
      toast(MEMBER_NOT_DELETABLE_ERR_MSG.label, { type: MEMBER_NOT_DELETABLE_ERR_MSG.type });
      return;
    }
    setSelectedMember(member);
    setShowMassDeleteWarningModal(true);
  };

  const handleCloseSwitchModal = (isRefreshNeeded = false) => {
    setShowInstructorSwitchModal(false);
    setShowStatusUpdateModal(false);
    setSelectedMember(null);
    if (typeof isRefreshNeeded == "boolean" && isRefreshNeeded) {
      fetchData(params);
      setCheckedEntries([]);
      setCheckAllIsChecked(false);
    }
  };

  const handleApplyMassDelete = (isConfirmed: boolean) => {
    if (isConfirmed) {
      let result = [];
      data?.forEach((member) => {
        if (checkedEntries.includes(member.id)) {
          if (MEMBER_STATUS_ALLOWED_FOR_DELETE.includes(member.status)) result.push(member.id);
        }
      });
      if (!result.length) {
        toast("None of the selected are deletable", { type: "info" });
        setShowMassDeleteWarningModal(false);
        return;
      }
      setLoading(true);
      let entriesToDelete = selectedMember ? [selectedMember?.id] : result;
      setSelectedMember(null);
      massDelete({ members: entriesToDelete })
        .then((_res) => {
          if (_res.success) {
            toast("Deleted successfully", { type: "success" });
            fetchData(params);
            setCheckedEntries([]);
            setCheckAllIsChecked(false);
          }
        })
        .catch((e) => {
          console.log(e);
          toast(MSG_OPERATION_FAILED, { type: "error" });
        })
        .finally(() => setLoading(false));
    }
    setSelectedMember(null);
    setShowMassDeleteWarningModal(false);
  };

  const handleToggleCheckAll = () => {
    setSelectedMember(null);
    if (checkAllIsChecked) {
      setCheckedEntries([]);
    } else {
      setCheckedEntries(data.map((m) => m.id));
    }
    setCheckAllIsChecked(!checkAllIsChecked);
  };

  const handleToggleCheckMember = (memberId: number) => {
    setSelectedMember(null);
    setCheckAllIsChecked(false);
    const i = checkedEntries.indexOf(memberId);
    if (i === -1) {
      setCheckedEntries([...checkedEntries, memberId]);
    } else {
      let entries = [...checkedEntries];
      entries.splice(i, 1);
      setCheckedEntries(entries);
    }
  };

  const onUpdateStatus = (id, value) => {
    return new Promise((resolve) => {
      updateMemberStatus({ member_id: id, member_status: value })
        .then((result) => {
          if (result.success) {
            //show success message
            toast("Member state updated successfully", { type: "success" });
            setData((p) =>
              p.map((member) => {
                if (member.id == id) member.status = value;
                return member;
              })
            );
          } else {
            toast("Member state update failed", { type: "error" });
          }
        })
        .catch((e) => {
          toast("Member state update failed", { type: "error" });
        })
        .finally(() => resolve(true));
    });
  };

  return (
    <div>
      <ActionButtons
        onMassStatusSwitchClick={handleMassStatusSwitch}
        onMassInstructorSwitchClick={handleMassInstructorSwitch}
        onMassDeleteClick={handleMassDelete}
      />

      <TableContainer className="staff-accounts-table">
        <Table aria-label="sticky table">
          <TableHeader
            allCheckFlag={checkAllIsChecked}
            handleAllCheck={handleToggleCheckAll}
            pageParams={{ sort_by: params.sort_by, sort_order: params.sort_order }}
            sortingCallback={handleSortChange}
          />
          <TableBody>
            {!loading &&
              data?.map((item) => (
                <TableItem
                  key={`member_${item.id}`}
                  data={item}
                  onUpdateStatus={onUpdateStatus}
                  isChecked={checkedEntries.includes(item.id)}
                  onCheckMember={handleToggleCheckMember}
                  onClickDelete={(member) => handleSignleEntryDelete(member)}
                  onClickInstructorSwitch={(member) => handleSignleEntryInstructorSwitch(member)}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {loading && (
        <Box mt="30px">
          <ScreenLoader isScreenLoader={true} />
        </Box>
      )}

      <Pagination
        {...pagination}
        onPageChange={handlePageChange}
        pageDropdownProps={{
          defaultValue: pagination.per_page,
          onChange: handlePaginationOptionChange,
          value: pagination.per_page,
        }}
      />
      <MassInstructorSwitchModal
        open={showInstructorSwitchModal}
        onClose={handleCloseSwitchModal}
        memberIds={selectedMember ? [selectedMember?.id] : checkedEntries}
        instructorOptions={instructorOptions.filter((f) => !f.isSelectAll)}
        moduleName="staff-details-assigned-members"
      />
      <MassStatusUpdateModal
        open={showStatusUpdateModal}
        onClose={handleCloseSwitchModal}
        memberIds={checkedEntries}
      />
      <ConfirmationBox
        open={showMassDeleteWarningModal}
        onClose={handleApplyMassDelete}
        mainTitle={"Confirm"}
        title={"Are you sure you want to delete the selected member(s)?"}
        message={<>Note that only members with status trial or demo will be deleted.</>}
        type={"confirmation"}
      />
    </div>
  );
};

export default AssignedMembers;
